// Copyright (c) Microsoft. All rights reserved.

import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
    //Button,
    FluentProvider,
    Link,
    Subtitle1,
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components';

import * as React from 'react';
import { useEffect } from 'react';
import { UserSettingsMenu } from './components/header/UserSettingsMenu';
// import { PluginGallery } from './components/open-api-plugins/PluginGallery';
import { BackendProbe, ChatView, Error, Loading, Login } from './components/views';
import { AuthHelper } from './libs/auth/AuthHelper';
import {
    useAdmin,
    useChat,
    useGraph,
    // useFile
} from './libs/hooks';
// import { AlertType } from './libs/models/AlertType';
import { useAppDispatch, useAppSelector } from './redux/app/hooks';
import { RootState } from './redux/app/store';
import { ActiveUserInfo, FeatureKeys } from './redux/features/app/AppState';
import {
    //addAlert,
    setActiveUserInfo,
    // setServiceInfo
} from './redux/features/app/appSlice';
import { semanticKernelDarkTheme, semanticKernelLightTheme, Breakpoints } from './styles';
import { AdminView } from './components/views/AdminView';
import { ITokenConsumer } from './libs/models/TokenUser';
import { useToken } from './libs/hooks/useToken';
import { UseChat } from './libs/hooks/useChat';
import { UseAdmin } from './libs/hooks/useAdmin';
import { AccountInfo } from '@azure/msal-browser';
import logo from './assets/logo/logo.jpg';
//import netafimLogo from './assets/logo/netafim-logo.svg';

export const useClasses = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        ...shorthands.overflow('hidden'),
    },
    header: {
        alignItems: 'center',
        backgroundImage: 'linear-gradient(-45deg, #fff 15%, #353750 15%)',
        color: '#fff',
        display: 'flex',
        '& h1': {
            paddingLeft: tokens.spacingHorizontalXL,
            display: 'flex',
        },
        height: '76px',
        justifyContent: 'space-between',
        width: '100%',
        boxShadow: '0 5px 16px 0 rgba(0, 0, 0, 0.06)',
        '@media (max-width: 1280px)': {
            backgroundImage: 'linear-gradient(-45deg, #fff 31%, #353750 25%)',
        },
    },
    persona: {
        marginRight: tokens.spacingHorizontalXXL,
    },
    cornerItems: {
        display: 'flex',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
    brandLogos: {
        marginRight: tokens.spacingHorizontalL,
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('6px'),
    },
    headerLogo: {
        fontWeight: 'bolder',
        marginRight: '1rem',
    },
    linkButton: {
        color: '#353750',
        fontWeight: 'bolder',
    },
    hideOnMobile: {
        ...Breakpoints.small({
            display: 'none',
        }),
    },
});

enum AppState {
    ProbeForBackend,
    SettingUserInfo,
    ErrorLoadingChats,
    ErrorLoadingUserInfo,
    LoadingChats,
    Chat,
    SigningOut,
    AdminCenter,
    RefreshInfo,
}

const App = () => {
    const classes = useClasses();

    const [appState, setAppState] = React.useState(AppState.ProbeForBackend);
    const dispatch = useAppDispatch();

    const { instance, inProgress } = useMsal();
    const { activeUserInfo, features } = useAppSelector((state: RootState) => state.app);
    const isAuthenticated = useIsAuthenticated();
    const admin: UseAdmin = useAdmin();
    const chat = useChat();
    const token = useToken();
    const msGraph = useGraph();

    useEffect(() => {
        const setUserInfo = async (account: AccountInfo) => {
            const accountFullName = account.name?.split('|')[0].trim() ?? account.username;
            const tokenConsumer: ITokenConsumer = {
                id: account.localAccountId,
                emailAddress: account.username,
                fullName: accountFullName,
                completionUsage: 0,
                promptUsage: 0,
                budgetAllocation: 0,
                budgetUsage: 0,
            };
            const allocation = await token.getConsumerAllocation(tokenConsumer);
            const profilePhoto = await msGraph.getProfilePhoto();

            dispatch(
                setActiveUserInfo({
                    id: account.homeAccountId,
                    externalId: account.localAccountId,
                    email: account.username,
                    username: accountFullName,
                    isAdmin: account.idTokenClaims?.roles?.includes('Admins') ?? false,
                    budgetAllocated: allocation.budgetAllocation,
                    budgetUsed: allocation.budgetUsage,
                    isMenuOpen: false,
                    profilePhoto: profilePhoto,
                }),
            );
        };

        if (isAuthenticated && appState === AppState.SettingUserInfo) {
            const account = instance.getActiveAccount();

            if (!account) {
                setAppState(AppState.ErrorLoadingUserInfo);
            } else {
                void setUserInfo(account).then(() => {
                    setAppState(AppState.LoadingChats);
                });
            }
        }

        if (appState === AppState.LoadingChats) {
            void Promise.all([
                activeUserInfo?.isAdmin
                    ? admin.initialize().then((succeeded) => {
                          if (succeeded) {
                              setAppState(AppState.AdminCenter);
                          }
                      })
                    : // Load all chats from memory
                      chat.loadChats().then((succeeded: boolean) => {
                          if (succeeded) {
                              setAppState(AppState.Chat);
                          }
                      }),
            ]);
        }

        if (appState === AppState.RefreshInfo) {
            const account = instance.getActiveAccount();
            if (!account || !activeUserInfo?.isAdmin) return;

            void setUserInfo(account).then(() => {
                void chat.loadChats().then((succeeded: boolean) => {
                    if (succeeded) {
                        setAppState(AppState.Chat);
                    }
                });
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance, inProgress, isAuthenticated, appState]);

    const content = (
        <Chat
            classes={classes}
            appState={appState}
            setAppState={setAppState}
            activeUserInfo={activeUserInfo}
            chat={chat}
            admin={admin}
        />
    );
    return (
        <FluentProvider
            className="app-container"
            theme={features[FeatureKeys.DarkMode].enabled ? semanticKernelDarkTheme : semanticKernelLightTheme}
        >
            {AuthHelper.isAuthAAD() ? (
                <>
                    <UnauthenticatedTemplate>
                        <div className={classes.container}>
                            <div className={classes.header}>
                                <Subtitle1 as="h1" className={classes.headerLogo}>
                                    NetafimAI
                                </Subtitle1>
                                <div className={classes.cornerItems}>
                                    <div className={classes.cornerItems}>
                                        <div className={classes.brandLogos}>
                                            <img className={classes.hideOnMobile} src={logo} width={120} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {appState === AppState.SigningOut && <Loading text="Signing you out..." />}
                            {appState !== AppState.SigningOut && <Login />}
                        </div>
                    </UnauthenticatedTemplate>
                    <AuthenticatedTemplate>{content}</AuthenticatedTemplate>
                </>
            ) : (
                content
            )}
        </FluentProvider>
    );
};

const Chat = ({
    classes,
    appState,
    setAppState,
    activeUserInfo,
    //chat,
    admin,
}: {
    classes: ReturnType<typeof useClasses>;
    appState: AppState;
    setAppState: (state: AppState) => void;
    activeUserInfo: ActiveUserInfo | undefined;
    chat: UseChat;
    admin: UseAdmin;
}) => {
    const onBackendFound = React.useCallback(() => {
        setAppState(
            AuthHelper.isAuthAAD()
                ? // if AAD is enabled, we need to set the active account before loading chats
                  AppState.SettingUserInfo
                : // otherwise, we can load chats immediately
                  AppState.LoadingChats,
        );
    }, [setAppState]);
    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <Subtitle1 className={classes.headerLogo} as="h1">
                    NetafimAI
                </Subtitle1>
                {appState > AppState.SettingUserInfo && (
                    <>
                        {activeUserInfo?.isAdmin && (
                            <Link
                                style={{ color: '#fff' }}
                                className={classes.linkButton}
                                onClick={() => {
                                    if (appState === AppState.AdminCenter) {
                                        setAppState(AppState.RefreshInfo);
                                    } else {
                                        void admin.initialize().then((succeeded: boolean) => {
                                            if (succeeded) {
                                                setAppState(AppState.AdminCenter);
                                            }
                                        });
                                    }
                                }}
                            >
                                {appState === AppState.AdminCenter ? 'Switch to Chat' : 'Switch to Admin Panel'}
                            </Link>
                        )}
                    </>
                )}
                <div className={classes.cornerItems}>
                    <div className={classes.cornerItems}>
                        <div className={classes.brandLogos}>
                            <img className={classes.hideOnMobile} src={logo} width={120} />
                            {appState > AppState.SettingUserInfo && (
                                <UserSettingsMenu
                                    setLoadingState={() => {
                                        setAppState(AppState.SigningOut);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {appState === AppState.ProbeForBackend && <BackendProbe onBackendFound={onBackendFound} />}
            {appState === AppState.SettingUserInfo && (
                <Loading text={'Hang tight while we fetch your information...'} />
            )}
            {appState === AppState.ErrorLoadingUserInfo && (
                <Error text={'Unable to load user info. Please try signing out and signing back in.'} />
            )}
            {appState === AppState.ErrorLoadingChats && (
                <Error text={'Unable to load chats. Please try refreshing the page.'} />
            )}
            {appState === AppState.LoadingChats && <Loading text="Loading chats..." />}
            {appState === AppState.Chat && <ChatView />}
            {appState === AppState.AdminCenter && <AdminView />}
        </div>
    );
};

export default App;
